/**
 * Fetch client for the Contractor Variation Platform's REST API.
 *
 * The TypeScript types are generated from the OpenAPI 3.1 spec at
 * ``evidence/S8/openapi.json`` via ``openapi-typescript`` (see
 * ``npm run openapi:generate``); the runtime here is a thin
 * ``fetch``-based wrapper. The generated file at
 * ``src/api/openapi.gen.ts`` carries the "do not edit" header;
 * regeneration produces a clean diff (S10.AC.3).
 */

import type { components } from "./openapi.gen";

type Schemas = components["schemas"];

export type DocumentRecord = Schemas["DocumentRecord"] & {
  // S16 (ADR-0016): the variation a supporting document was attached
  // under, for display and grouping. Extended here rather than via a
  // codegen because the pinned snapshot predates it.
  variation_ref?: string | null;
};
export type AssessmentRecord = Schemas["AssessmentRecord"] & {
  // The OpenAPI snapshot was taken before the issue endpoint was
  // wired; extend the type here so the UI's strict types know about
  // the new fields without forcing a full codegen on every iteration.
  issued_at?: string | null;
  issued_by?: string | null;
  // Live progress fields populated by the runner. The SPA renders
  // ``phase_detail`` as operator-facing text alongside the spinner
  // so a long-running assessment is no longer a black box.
  phase?: string | null;
  phase_detail?: string | null;
  // Source document ids carried on the record. The pinned snapshot
  // predates them; the augment path (S16) reuses them when re-running.
  base_contract_document_id?: string | null;
  submission_document_id?: string | null;
  // S16 augment lineage (ADR-0018). ``revision`` is 1 for a fresh run
  // and increments for each augmented re-run; ``parent_assessment_id``
  // links a revision to the run it augments;
  // ``additional_context_label`` records what extra context (Assistant
  // chat, prior determination) was carried in.
  parent_assessment_id?: string | null;
  revision?: number;
  additional_context_label?: string;
};
export type AssessmentStatus = Schemas["AssessmentStatus"];
export type AssessmentCreateRequest = Schemas["AssessmentCreateRequest"] & {
  // S16: operator chat carry-over (ADR-0017) and augment lineage
  // (ADR-0018). All optional; a plain run omits them.
  additional_context?: string;
  additional_context_label?: string;
  parent_assessment_id?: string | null;
};
export type WorkflowTemplate = AssessmentCreateRequest["workflow_template"];

export interface ApiError {
  code: string;
  message: string;
  status: number;
}

export type HealthResponse = Schemas["HealthResponse"];
export type VersionResponse = Schemas["VersionResponse"];

export interface ClientConfig {
  baseUrl: string;
  token: string;
}

/** The only base URL the platform supports, in every environment.
 *
 * Production serves the SPA on the Pages domain and proxies `/api/*`
 * to Fly through `functions/api/[[path]].ts`; development serves it
 * on the Vite dev server and proxies the same prefix through
 * `vite.config.ts`. Both exist so the browser only ever sees one
 * origin. Nothing reads `VITE_API_BASE_URL`.
 */
export const DEFAULT_BASE_URL = "/api";

/**
 * Reduce a stored base URL to one that can actually reach the API.
 *
 * A cross-origin base URL cannot work, by construction: the backend
 * mounts no CORS middleware (deliberately, because the same-origin
 * proxy makes it unnecessary), and every request here carries an
 * `Authorization` header, which makes it non-simple and forces a
 * preflight the API answers with 405. The browser then rejects the
 * request before it is sent and `fetch` throws "Failed to fetch".
 *
 * That used to be unrecoverable from inside the app. Every panel
 * load and every status poll failed identically, so the operator saw
 * a wall of "Load failed: Failed to fetch" and the only ways back
 * were DevTools or knowing to reopen a collapsed Settings section.
 * Worse, the field that produces this value was typed `url`, which
 * invites exactly the absolute address that breaks it.
 *
 * So a value that cannot work is discarded rather than honoured.
 * The app repairs itself on the next load.
 *
 * @param stored The raw localStorage value, or null when unset.
 * @param origin The page's own origin. Defaults to
 *   `window.location.origin`; passed explicitly by the unit tests,
 *   which run under Vitest's `node` environment where there is no
 *   `window` (adding a DOM environment would mean a new dependency
 *   for one comparison).
 * @returns A usable base URL: the stored one when it is same-origin,
 *   otherwise {@link DEFAULT_BASE_URL}.
 *
 * @example
 * normaliseBaseUrl(null)                        // "/api"
 * normaliseBaseUrl("  ")                        // "/api"
 * normaliseBaseUrl("/api/")                     // "/api"
 * normaliseBaseUrl("https://cvp-api.fly.dev")   // "/api"  (cross-origin)
 * normaliseBaseUrl("//evil.example")            // "/api"  (protocol-relative)
 */
export function normaliseBaseUrl(
  stored: string | null,
  origin?: string,
): string {
  if (stored === null) return DEFAULT_BASE_URL;
  const trimmed = stored.trim();
  // An empty string is not caught by `??`, so a cleared Settings
  // field used to persist "" and send every request to the SPA's own
  // root instead of the proxy prefix.
  if (!trimmed) return DEFAULT_BASE_URL;
  // Protocol-relative ("//host") starts with "/" but is cross-origin
  // to the browser, so it has to be rejected before the relative-path
  // check below would wave it through.
  if (trimmed.startsWith("//")) return DEFAULT_BASE_URL;
  // Strip trailing slashes so callers concatenating "/v1/..." cannot
  // produce a double slash.
  const withoutTrailingSlash = trimmed.replace(/\/+$/, "");
  if (!withoutTrailingSlash) return DEFAULT_BASE_URL;
  if (withoutTrailingSlash.startsWith("/")) return withoutTrailingSlash;
  // Anything else must parse as a URL on this page's own origin.
  const pageOrigin =
    origin ??
    (typeof window === "undefined" ? undefined : window.location.origin);
  if (pageOrigin === undefined) return DEFAULT_BASE_URL;
  try {
    const parsed = new URL(withoutTrailingSlash);
    if (parsed.origin === pageOrigin) return withoutTrailingSlash;
  } catch {
    // Not a URL at all (a bare hostname, a typo). Unusable either way.
  }
  return DEFAULT_BASE_URL;
}

/**
 * Read the auth/base-URL configuration from localStorage.
 *
 * The Settings page is the single producer of these values; the
 * client is the single consumer. The stored base URL is passed
 * through {@link normaliseBaseUrl} so a value that cannot reach the
 * API never reaches the client.
 */
export function loadConfig(): ClientConfig {
  return {
    baseUrl: normaliseBaseUrl(localStorage.getItem("cvp.api.baseUrl")),
    token: localStorage.getItem("cvp.api.token") ?? "demo-token",
  };
}

/**
 * Persist the auth/base-URL configuration to localStorage.
 *
 * Triggered by the Settings page on save. Fields are stored as plain
 * strings; the bearer token is treated as opaque. The client never
 * logs the token to the audit pipeline.
 */
export function saveConfig(cfg: ClientConfig): void {
  // Normalise on the way in as well as on the way out, so a value
  // that cannot reach the API is never persisted in the first place
  // and Settings "Save" repairs a browser that already holds one.
  localStorage.setItem("cvp.api.baseUrl", normaliseBaseUrl(cfg.baseUrl));
  localStorage.setItem("cvp.api.token", cfg.token);
}

const COOKIE_NAME_CSRF = "cvp_csrf";

function readCookie(name: string): string | null {
  for (const raw of document.cookie.split(";")) {
    const trimmed = raw.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

const STATE_MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function request<T>(
  cfg: ClientConfig,
  path: string,
  init?: RequestInit & { authenticated?: boolean; timeoutMs?: number },
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.authenticated !== false) {
    headers.set("Authorization", `Bearer ${cfg.token}`);
  }
  // Attach the CSRF header when the request is state-mutating.
  // The cookie-aware backend dependency (build_auth_dependency)
  // requires this whenever the cvp_session cookie is the
  // resolved credential. Bearer-only callers are unaffected.
  const method = (init?.method ?? "GET").toUpperCase();
  if (STATE_MUTATING_METHODS.has(method)) {
    const csrfToken = readCookie(COOKIE_NAME_CSRF);
    if (csrfToken !== null) {
      headers.set("X-CVP-CSRF", csrfToken);
    }
  }
  // Bound the request with an AbortController when the caller asks for
  // a timeout and has not supplied its own signal. Without this a hung
  // connection (a status poll whose socket stalls) waits forever, which
  // is exactly how the assessment indicator could freeze on a stale
  // "Running". The timer is always cleared in the finally, so a fast
  // response leaks nothing.
  const timeoutMs = init?.timeoutMs;
  let signal = init?.signal ?? undefined;
  let timeoutId: number | null = null;
  if (
    signal === undefined &&
    timeoutMs !== undefined &&
    timeoutMs > 0 &&
    typeof AbortController !== "undefined"
  ) {
    const controller = new AbortController();
    signal = controller.signal;
    timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  }
  try {
    const response = await fetch(`${cfg.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: "include",
      signal,
    });
    const text = await response.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        // The proxy is returning a non-JSON body (Cloudflare 502 / 524
        // error page, Fly 504 timeout, etc.). Preserve the status code
        // for the caller to act on, but never let raw HTML reach the
        // toast; the operator does not need to read a full error
        // page in a chat-failed banner. A status-code-derived label
        // covers the common cases and keeps the message bounded.
        const label =
          response.status === 502
            ? "Upstream API unreachable"
            : response.status === 503
              ? "Upstream API unavailable"
              : response.status === 504 || response.status === 524
                ? "The request took too long. Try a shorter question or retry in a moment."
                : `${response.status} ${response.statusText || "transport error"}`;
        body = { code: "transport_error", message: label };
      }
    }
    if (!response.ok) {
      const err = body as { code?: string; message?: string } | null;
      throw {
        code: err?.code ?? "error",
        message: err?.message ?? response.statusText,
        status: response.status,
      } satisfies ApiError;
    }
    return body as T;
  } catch (err) {
    // A thrown ApiError from the !response.ok branch above is already
    // in wire shape; rethrow it untouched rather than double-wrapping.
    if (
      err !== null &&
      typeof err === "object" &&
      "code" in err &&
      typeof (err as { code: unknown }).code === "string"
    ) {
      throw err;
    }
    // Our timeout (or a caller abort) surfaces as an AbortError. Give
    // the operator an actionable, bounded message instead of a raw DOM
    // exception; status 0 marks "no HTTP response was received".
    if (err instanceof Error && err.name === "AbortError") {
      throw {
        code: "timeout",
        message: "The request timed out. Retrying shortly.",
        status: 0,
      } satisfies ApiError;
    }
    // Any other rejection is a transport-level failure (DNS, refused
    // connection, offline). Normalise it to the same ApiError shape so
    // every caller has one error contract to handle.
    throw {
      code: "network_error",
      message:
        err instanceof Error && err.message ? err.message : "Network error",
      status: 0,
    } satisfies ApiError;
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
}

export async function getHealth(cfg: ClientConfig): Promise<HealthResponse> {
  return request<HealthResponse>(cfg, "/v1/health", { authenticated: false });
}

export async function getVersion(cfg: ClientConfig): Promise<VersionResponse> {
  return request<VersionResponse>(cfg, "/v1/version", { authenticated: false });
}

/**
 * Upload a document.
 *
 * The server accepts multipart/form-data with a ``file`` part and an
 * optional ``doc_type`` form field (e.g. ``base_contract``,
 * ``variation_submission``). The client returns the platform's
 * stable :type:`DocumentRecord`.
 */
export async function uploadDocument(
  cfg: ClientConfig,
  file: File,
  docType: string,
  options?: { contractId?: string | null; variationRef?: string | null },
): Promise<DocumentRecord> {
  const form = new FormData();
  form.append("file", file);
  form.append("doc_type", docType);
  if (options?.contractId) {
    form.append("contract_id", options.contractId);
  }
  // S16 (ADR-0016): a supporting document attached to a variation
  // carries the variation submission's document id so the Documents
  // panel can flag it with its owning variation. Display only; it
  // does not change the contract-wide retrieval scope.
  if (options?.variationRef) {
    form.append("variation_ref", options.variationRef);
  }
  return request<DocumentRecord>(cfg, "/v1/documents", {
    method: "POST",
    body: form,
  });
}

export async function getDocument(
  cfg: ClientConfig,
  documentId: string,
): Promise<DocumentRecord> {
  return request<DocumentRecord>(cfg, `/v1/documents/${encodeURIComponent(documentId)}`);
}

export async function createAssessment(
  cfg: ClientConfig,
  payload: AssessmentCreateRequest,
): Promise<AssessmentRecord> {
  return request<AssessmentRecord>(cfg, "/v1/assessments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function getAssessment(
  cfg: ClientConfig,
  assessmentId: string,
  timeoutMs?: number,
): Promise<AssessmentRecord> {
  return request<AssessmentRecord>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}`,
    timeoutMs !== undefined ? { timeoutMs } : undefined,
  );
}

/**
 * Fetch one of the three rendered PDFs for a completed assessment.
 *
 * Returns a Blob the caller can pipe into a download or a viewer.
 * Returns ``null`` when the assessment is not yet complete (HTTP
 * 409 from the server); the caller surfaces a "still rendering"
 * state.
 */
export async function listDocuments(cfg: ClientConfig): Promise<DocumentRecord[]> {
  return request<DocumentRecord[]>(cfg, "/v1/documents");
}

export async function deleteDocument(
  cfg: ClientConfig,
  documentId: string,
): Promise<void> {
  await request<void>(
    cfg,
    `/v1/documents/${encodeURIComponent(documentId)}`,
    { method: "DELETE" },
  );
}

export async function listAssessments(cfg: ClientConfig): Promise<AssessmentRecord[]> {
  return request<AssessmentRecord[]>(cfg, "/v1/assessments");
}

export interface DeterminationPayload {
  determination: Record<string, unknown> & {
    determination_id: string;
    variation_id: string;
    change_classification: string;
    determination_outcome: string;
    recommendation: string;
    final_assessed_value: string;
    contract_sum_adjustment: string;
    entitlement_finding: {
      claim_summary: string;
      basis_clause: string;
      outcome: string;
      reasoning: string;
      citations: { chunk_id: string; document_id: string; page: number; excerpt: string }[];
    };
    valuation_lines: {
      cost_element: string;
      methodology: string;
      claimed_amount: string;
      assessed_amount: string;
      contract_basis: string;
      notes?: string;
      citations: { chunk_id: string; document_id: string; page: number; excerpt: string }[];
    }[];
    time_impact: {
      eot_claimed: boolean;
      critical_path: boolean;
      entitled: boolean;
      assessed_delay_days: number;
      concurrent_delay: boolean;
      time_clause: string;
      citations: { chunk_id: string; document_id: string; page: number; excerpt: string }[];
    };
    non_compliance: {
      category: string;
      description: string;
      citations: { chunk_id: string; document_id: string; page: number; excerpt: string }[];
    }[];
    telemetry: {
      input_tokens: number;
      output_tokens: number;
      tool_calls: number;
      elapsed_ms: number;
      model: string;
      cost_usd: string;
    };
  };
  verdicts: {
    field_label: string;
    chunk_id: string;
    deterministic: string;
    judge: string;
    combined: string;
    det_similarity: number;
    judge_rationale: string;
  }[];
  overall_confidence: number;
  retries_consumed: number;
  signature: string;
}

export async function getDetermination(
  cfg: ClientConfig,
  assessmentId: string,
): Promise<DeterminationPayload> {
  return request<DeterminationPayload>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}/determination`,
  );
}

export interface DeterminationEdit {
  recommendation?: string;
  determination_outcome?:
    | "approved"
    | "partially_approved"
    | "rejected"
    | "no_variation"
    | "human_review_required";
  final_assessed_value?: string;
  contract_sum_adjustment?: string;
}

export async function patchDetermination(
  cfg: ClientConfig,
  assessmentId: string,
  body: DeterminationEdit,
): Promise<DeterminationPayload> {
  return request<DeterminationPayload>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}/determination`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export interface IssueDeterminationResult {
  record: AssessmentRecord;
  audit_record_id: string;
  pdfs_rerendered: boolean;
}

export async function issueDetermination(
  cfg: ClientConfig,
  assessmentId: string,
  issuedBy: string,
): Promise<IssueDeterminationResult> {
  return request<IssueDeterminationResult>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}/issue`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ issued_by: issuedBy }),
    },
  );
}

// ----- Two-stage approval workflow -----------------------------------

export interface ApprovalActionResult {
  record: AssessmentRecord;
  audit_record_id: string;
}

export async function sendForApproval(
  cfg: ClientConfig,
  assessmentId: string,
  body: { approver_email: string; note?: string },
): Promise<ApprovalActionResult> {
  return request<ApprovalActionResult>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}/send-for-approval`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        approver_email: body.approver_email,
        note: body.note ?? "",
      }),
    },
  );
}

export async function approveDetermination(
  cfg: ClientConfig,
  assessmentId: string,
  approvedBy: string,
): Promise<ApprovalActionResult> {
  return request<ApprovalActionResult>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}/approve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ approved_by: approvedBy }),
    },
  );
}

export async function requestChangesOnDetermination(
  cfg: ClientConfig,
  assessmentId: string,
  comment: string,
): Promise<ApprovalActionResult> {
  return request<ApprovalActionResult>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}/request-changes`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ comment }),
    },
  );
}

export interface DeliverDeterminationResult {
  delivery_record_id: string;
  audit_record_id: string;
  delivered_at: string;
  contractor_email: string;
}

export async function deliverDetermination(
  cfg: ClientConfig,
  assessmentId: string,
  payload: { contractor_email: string; contractor_name?: string; note?: string },
): Promise<DeliverDeterminationResult> {
  return request<DeliverDeterminationResult>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}/deliver`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contractor_email: payload.contractor_email,
        contractor_name: payload.contractor_name ?? "",
        note: payload.note ?? "",
      }),
    },
  );
}

// ---------------------------------------------------------------------------
// Chat: per-variation assistant sessions.
// The shapes mirror src/cvp/api/chat.py; we hand-author them here so the UI
// can ship without an OpenAPI regen on every chat-shape tweak.
// ---------------------------------------------------------------------------

export interface ChatCitation {
  chunk_id: string;
  filename: string;
  page: number;
  section: string;
  excerpt: string;
}

export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  at: string;
  input_tokens: number;
  output_tokens: number;
  citations: ChatCitation[];
}

// The chat surface a session belongs to. "workspace" = per-variation
// chats in the variation workspace; "contractor" = Tab-2 per-contract
// chats. This is the explicit discriminator the history list filters
// on (see ADR-0015); it replaces the old "contract_id present => Tab 2"
// inference, so a workspace chat may now carry a contract_id for
// retrieval scoping without being misfiled.
export type ChatKind = "workspace" | "contractor";

export interface ChatSession {
  session_id: string;
  tenant_id: string;
  title: string;
  variation_label: string;
  variation_brief: Record<string, unknown>;
  assessment_id: string | null;
  kind: ChatKind;
  created_at: string;
  updated_at: string;
  messages: ChatMessage[];
}

export interface ChatSessionSummary {
  session_id: string;
  title: string;
  variation_label: string;
  assessment_id: string | null;
  // Surface discriminator the SPA filters on to keep the Workspace
  // and Tab-2 flows apart.
  kind: ChatKind;
  // Retrieval scope only (the contract whose corpus this chat
  // searches), sourced from the brief; no longer the surface
  // discriminator. May be set on a Workspace chat.
  contract_id: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ChatSessionsResponse {
  sessions: ChatSessionSummary[];
}

export interface ChatMessageResponse {
  session: ChatSession;
  user_message: ChatMessage;
  assistant_message: ChatMessage;
}

export async function listChatSessions(
  cfg: ClientConfig,
): Promise<ChatSessionSummary[]> {
  const response = await request<ChatSessionsResponse>(cfg, "/v1/chat/sessions");
  return response.sessions;
}

export interface PendingApprovalRow {
  assessment_id: string;
  variation_id: string;
  contract_id: string;
  contractor_name: string;
  approver_email: string;
  approver_display_name: string;
  prepared_by: string;
  approval_sent_at: string | null;
}

export interface PendingApprovalsResponse {
  rows: PendingApprovalRow[];
}

export async function listPendingApprovals(
  cfg: ClientConfig,
): Promise<PendingApprovalRow[]> {
  const response = await request<PendingApprovalsResponse>(
    cfg,
    "/v1/approvals/pending",
  );
  return response.rows;
}

export async function createChatSession(
  cfg: ClientConfig,
  payload: {
    title?: string;
    variation_label?: string;
    variation_brief?: Record<string, unknown>;
    assessment_id?: string | null;
    kind?: ChatKind;
  },
): Promise<ChatSession> {
  return request<ChatSession>(cfg, "/v1/chat/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title: payload.title ?? "New variation",
      variation_label: payload.variation_label ?? "",
      variation_brief: payload.variation_brief ?? {},
      assessment_id: payload.assessment_id ?? null,
      kind: payload.kind ?? "workspace",
    }),
  });
}

// Note on atomicity: when re-pointing a live session at a different
// variation, send variation_brief AND assessment_id together (the
// matching assessment, or null when no determination has been run).
// The backend rejects a brief change that alters the variation
// identity without an explicit assessment_id (HTTP 422), so retrieval
// scope and the embedded determination can never diverge. See ADR-0015.
export async function patchChatSession(
  cfg: ClientConfig,
  sessionId: string,
  body: {
    assessment_id?: string | null;
    title?: string;
    variation_label?: string;
    variation_brief?: Record<string, unknown>;
  },
): Promise<ChatSession> {
  return request<ChatSession>(
    cfg,
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function getChatSession(
  cfg: ClientConfig,
  sessionId: string,
): Promise<ChatSession> {
  return request<ChatSession>(
    cfg,
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}`,
  );
}

export async function sendChatMessage(
  cfg: ClientConfig,
  sessionId: string,
  content: string,
): Promise<ChatMessageResponse> {
  return request<ChatMessageResponse>(
    cfg,
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    },
  );
}

// Re-run the last unanswered message in a session without resending the
// text. The server re-runs the persisted last user turn (it was saved
// before the failed attempt), so this needs no body and creates no
// duplicate turn. Use after a transient failure such as a 529 overload;
// the server returns 409 if the last turn already has a reply.
export async function retryChatMessage(
  cfg: ClientConfig,
  sessionId: string,
): Promise<ChatMessageResponse> {
  return request<ChatMessageResponse>(
    cfg,
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages/retry`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
  );
}

// ----- Streaming chat (ADR-0022) -------------------------------------
//
// The streaming variants below POST to the SAME send/retry endpoints
// as ``sendChatMessage`` / ``retryChatMessage`` but negotiate a
// Server-Sent Events response with ``Accept: text/event-stream``. The
// server-side ``CVP_CHAT_STREAMING`` flag governs the response shape,
// so the SPA does not need to know the flag: when streaming is OFF the
// route still answers the buffered JSON ``ChatMessageResponse`` and we
// resolve with it unchanged (the flag-off path); when ON the route
// answers ``text/event-stream`` and we parse it. EventSource cannot be
// used here because it is GET-only and cannot carry the bearer token,
// CSRF header, or credentials this API requires, so we drive a
// ``fetch`` + ``ReadableStream`` reader by hand and reuse the exact
// auth/CSRF/credentials posture of ``request()``.
//
// WIRE FORMAT consumed (must match src/cvp/api/chat.py's SSE route):
//   - ``event: delta``  data: {"text": "<chunk>"}        zero or more
//   - ``: heartbeat``   (a bare SSE comment line)         keep-alive only
//   - ``event: error``  data: {"code": "...","message":"..."}  terminal
//   - ``event: done``   data: <ChatMessageResponse JSON>  terminal, success
// Events are separated by a blank line; ``data:`` lines for one event
// are concatenated with newlines per the SSE spec. A ``done`` event
// resolves the promise with the authoritative session (it carries the
// server-stamped assistant message and merged citations). An ``error``
// event, or a stream that ends without a terminal event, rejects with
// an ApiError so the caller's catch handles it identically to the
// buffered path.

// Payload of an ``event: delta`` frame.
interface StreamDeltaPayload {
  text: string;
}

// Callbacks the streaming send/retry surface to the caller. ``onDelta``
// fires for each token chunk as it arrives; the returned promise
// resolves with the final ``ChatMessageResponse`` (parsed from the
// terminal ``done`` event, or from a buffered JSON body when streaming
// is disabled server-side).
export interface ChatStreamHandlers {
  onDelta: (text: string) => void;
}

const CHAT_STREAM_ACCEPT = "text/event-stream";

// Open a state-mutating POST that mirrors ``request()``'s auth, CSRF and
// credentials handling but asks for an event stream and returns the raw
// Response so the caller can read its body progressively. The body is
// optional (the retry endpoint takes none).
async function openChatStream(
  cfg: ClientConfig,
  path: string,
  body?: string,
): Promise<Response> {
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${cfg.token}`);
  headers.set("Accept", CHAT_STREAM_ACCEPT);
  if (body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  // POST is state-mutating, so attach the CSRF header exactly as
  // ``request()`` does whenever the cvp_csrf cookie is present; the
  // cookie-aware backend dependency requires it when the session
  // cookie is the resolved credential.
  const csrfToken = readCookie(COOKIE_NAME_CSRF);
  if (csrfToken !== null) {
    headers.set("X-CVP-CSRF", csrfToken);
  }
  return fetch(`${cfg.baseUrl}${path}`, {
    method: "POST",
    headers,
    body,
    credentials: "include",
  });
}

// Turn a transport-level failure into the same ApiError shape the
// buffered ``request()`` raises, so callers handle pre-stream and
// in-stream failures with one catch. Mirrors request()'s status-code
// labelling for non-JSON proxy error pages.
function chatStreamError(status: number, statusText: string, body: unknown): ApiError {
  const err = body as { code?: string; message?: string } | null;
  const label =
    status === 502
      ? "Upstream API unreachable"
      : status === 503
        ? "Upstream API unavailable"
        : status === 504 || status === 524
          ? "The request took too long. Try a shorter question or retry in a moment."
          : `${status} ${statusText || "transport error"}`;
  return {
    code: err?.code ?? "transport_error",
    message: err?.message ?? label,
    status,
  } satisfies ApiError;
}

// Parse one fully-buffered SSE event block (text between blank lines)
// into its event name and concatenated data payload. Returns ``null``
// for a block that carries only comments/heartbeats (no data), which
// the caller ignores. Per the SSE spec, leading-space after the colon
// is stripped and multiple ``data:`` lines join with newlines.
function parseSseEvent(block: string): { event: string; data: string } | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const rawLine of block.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line === "" || line.startsWith(":")) {
      // Blank line within a block cannot occur (blocks split on blank
      // lines); a leading colon is a comment line (our heartbeat).
      continue;
    }
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) {
      value = value.slice(1);
    }
    if (field === "event") {
      event = value;
    } else if (field === "data") {
      dataLines.push(value);
    }
  }
  if (dataLines.length === 0) {
    return null;
  }
  return { event, data: dataLines.join("\n") };
}

// Drive a fetch + ReadableStream reader over an SSE response, firing
// ``onDelta`` per token chunk and resolving with the final
// ChatMessageResponse from the terminal ``done`` event. ``error``
// events and a premature end both reject with an ApiError.
async function consumeChatStream(
  response: Response,
  handlers: ChatStreamHandlers,
): Promise<ChatMessageResponse> {
  if (response.body === null) {
    throw chatStreamError(response.status, response.statusText, null);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: ChatMessageResponse | null = null;
  let streamError: ApiError | null = null;
  // Process every complete event block currently in the buffer. SSE
  // separates events with a blank line (\n\n); a final partial block
  // stays buffered until more bytes arrive.
  const drain = (): void => {
    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const parsed = parseSseEvent(block);
      if (parsed !== null) {
        if (parsed.event === "delta") {
          const payload = JSON.parse(parsed.data) as StreamDeltaPayload;
          handlers.onDelta(payload.text);
        } else if (parsed.event === "done") {
          result = JSON.parse(parsed.data) as ChatMessageResponse;
        } else if (parsed.event === "error") {
          const body = JSON.parse(parsed.data) as { code?: string; message?: string };
          streamError = {
            code: body.code ?? "stream_error",
            message: body.message ?? "The assistant stopped unexpectedly.",
            status: 503,
          } satisfies ApiError;
        }
      }
      sep = buffer.indexOf("\n\n");
    }
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (value !== undefined) {
      buffer += decoder.decode(value, { stream: true });
      drain();
    }
    if (done) {
      break;
    }
    // A terminal event lets us stop reading without waiting for the
    // server to close the connection.
    if (result !== null || streamError !== null) {
      await reader.cancel();
      break;
    }
  }
  if (streamError !== null) {
    throw streamError;
  }
  if (result === null) {
    // The stream ended without a done/error event: the upstream was
    // cut mid-flight. Surface it as a transport error so the composer
    // offers a retry.
    throw {
      code: "stream_incomplete",
      message: "The connection closed before the assistant finished. Please retry.",
      status: 503,
    } satisfies ApiError;
  }
  return result;
}

// Read a pre-stream (non-SSE) response: either the buffered JSON
// ChatMessageResponse (streaming disabled server-side), or a JSON
// error body for a failure that happened before the first byte. This
// is the flag-off path and the pre-first-byte-503 path from ADR-0022.
async function readBufferedChatResponse(
  response: Response,
): Promise<ChatMessageResponse> {
  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }
  if (!response.ok) {
    throw chatStreamError(response.status, response.statusText, body);
  }
  return body as ChatMessageResponse;
}

// Dispatch on the negotiated response content-type: an event stream is
// consumed progressively; anything else is read as a buffered JSON
// reply (the flag-off path) or a pre-first-byte error.
async function dispatchChatResponse(
  response: Response,
  handlers: ChatStreamHandlers,
): Promise<ChatMessageResponse> {
  const contentType = response.headers.get("Content-Type") ?? "";
  if (response.ok && contentType.includes("text/event-stream")) {
    return consumeChatStream(response, handlers);
  }
  return readBufferedChatResponse(response);
}

// Streaming sibling of ``sendChatMessage``. Negotiates SSE; fires
// ``onDelta`` per token; resolves with the final ChatMessageResponse.
// Falls back transparently to the buffered reply when streaming is
// disabled server-side, so the call site is identical regardless of
// the flag.
export async function sendChatMessageStream(
  cfg: ClientConfig,
  sessionId: string,
  content: string,
  handlers: ChatStreamHandlers,
): Promise<ChatMessageResponse> {
  const response = await openChatStream(
    cfg,
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
    JSON.stringify({ content }),
  );
  return dispatchChatResponse(response, handlers);
}

// Streaming sibling of ``retryChatMessage``. No body, same negotiation
// and fallback behaviour as ``sendChatMessageStream``.
export async function retryChatMessageStream(
  cfg: ClientConfig,
  sessionId: string,
  handlers: ChatStreamHandlers,
): Promise<ChatMessageResponse> {
  const response = await openChatStream(
    cfg,
    `/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages/retry`,
  );
  return dispatchChatResponse(response, handlers);
}

// ----- Contractor / Contract / Contact ------------------------------
//
// The new tabs UI hangs off a small set of CRUD endpoints. Every
// list uses ``items: [...]`` to leave room for pagination metadata
// later without breaking the wire shape.

export interface ContractorOut {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface ContractOut {
  id: string;
  contractor_id: string;
  document_id: string | null;
  reference: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface ContactOut {
  id: string;
  contractor_id: string | null;
  contract_id: string | null;
  name: string;
  role: string;
  email: string;
  phone: string;
  notes: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApprovedVariationOut {
  assessment_id: string;
  variation_id: string;
  issued_at: string | null;
  issued_by: string | null;
  determination_outcome: string | null;
}

export interface ContactCreate {
  contractor_id?: string | null;
  contract_id?: string | null;
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  notes?: string;
  is_primary?: boolean;
}

export interface ContactPatch {
  contractor_id?: string | null;
  contract_id?: string | null;
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
  notes?: string;
  is_primary?: boolean;
}

export async function listContractors(
  cfg: ClientConfig,
): Promise<ContractorOut[]> {
  const r = await request<{ items: ContractorOut[] }>(cfg, "/v1/contractors");
  return r.items;
}

export async function createContractor(
  cfg: ClientConfig,
  name: string,
): Promise<ContractorOut> {
  return request<ContractorOut>(cfg, "/v1/contractors", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}

export async function updateContractor(
  cfg: ClientConfig,
  contractorId: string,
  name: string,
): Promise<ContractorOut> {
  return request<ContractorOut>(
    cfg,
    `/v1/contractors/${encodeURIComponent(contractorId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    },
  );
}

export async function deleteContractor(
  cfg: ClientConfig,
  contractorId: string,
): Promise<void> {
  await request<unknown>(
    cfg,
    `/v1/contractors/${encodeURIComponent(contractorId)}`,
    { method: "DELETE" },
  );
}

export async function listContracts(
  cfg: ClientConfig,
  options?: { contractorId?: string },
): Promise<ContractOut[]> {
  const qs = options?.contractorId
    ? `?contractor_id=${encodeURIComponent(options.contractorId)}`
    : "";
  const r = await request<{ items: ContractOut[] }>(
    cfg,
    `/v1/contracts${qs}`,
  );
  return r.items;
}

export async function createContract(
  cfg: ClientConfig,
  body: {
    contractor_id: string;
    reference: string;
    name?: string;
    document_id?: string | null;
  },
): Promise<ContractOut> {
  return request<ContractOut>(cfg, "/v1/contracts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateContract(
  cfg: ClientConfig,
  contractId: string,
  body: {
    contractor_id?: string;
    reference?: string;
    name?: string;
    document_id?: string | null;
  },
): Promise<ContractOut> {
  return request<ContractOut>(
    cfg,
    `/v1/contracts/${encodeURIComponent(contractId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteContract(
  cfg: ClientConfig,
  contractId: string,
): Promise<void> {
  await request<unknown>(
    cfg,
    `/v1/contracts/${encodeURIComponent(contractId)}`,
    { method: "DELETE" },
  );
}

export async function listApprovedVariations(
  cfg: ClientConfig,
  contractId: string,
): Promise<ApprovedVariationOut[]> {
  const r = await request<{ items: ApprovedVariationOut[] }>(
    cfg,
    `/v1/contracts/${encodeURIComponent(contractId)}/variations`,
  );
  return r.items;
}

export async function listContacts(
  cfg: ClientConfig,
  options?: {
    contractorId?: string | null;
    contractId?: string | null;
    side?: "principal" | "contractor";
  },
): Promise<ContactOut[]> {
  const params = new URLSearchParams();
  if (options?.contractorId) params.set("contractor_id", options.contractorId);
  if (options?.contractId) params.set("contract_id", options.contractId);
  if (options?.side) params.set("side", options.side);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const r = await request<{ items: ContactOut[] }>(cfg, `/v1/contacts${qs}`);
  return r.items;
}

export async function createContact(
  cfg: ClientConfig,
  body: ContactCreate,
): Promise<ContactOut> {
  return request<ContactOut>(cfg, "/v1/contacts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateContact(
  cfg: ClientConfig,
  contactId: string,
  body: ContactPatch,
): Promise<ContactOut> {
  return request<ContactOut>(
    cfg,
    `/v1/contacts/${encodeURIComponent(contactId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteContact(
  cfg: ClientConfig,
  contactId: string,
): Promise<void> {
  await request<unknown>(
    cfg,
    `/v1/contacts/${encodeURIComponent(contactId)}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------

// The briefing note now ships as a Word .docx (ADR-0008); the
// determination letter and evidence pack remain PDFs. The artefact
// fetcher derives the URL extension from the artefact name so the
// UI does not need to know the mapping at the call site.
type ArtifactKind =
  | "briefing-note"
  | "determination-letter"
  | "evidence-pack";

export function artifactExtension(artifact: ArtifactKind): "docx" | "pdf" {
  return artifact === "briefing-note" ? "docx" : "pdf";
}

export async function fetchArtifact(
  cfg: ClientConfig,
  assessmentId: string,
  artifact: ArtifactKind,
): Promise<Blob | null> {
  const ext = artifactExtension(artifact);
  const response = await fetch(
    `${cfg.baseUrl}/v1/assessments/${encodeURIComponent(assessmentId)}/${artifact}.${ext}`,
    {
      headers: { Authorization: `Bearer ${cfg.token}` },
    },
  );
  if (response.status === 409) {
    return null;
  }
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { code?: string; message?: string };
      detail = body.message ?? detail;
    } catch {
      // body not JSON; keep statusText
    }
    throw {
      code: "fetch_failed",
      message: detail,
      status: response.status,
    } satisfies ApiError;
  }
  return response.blob();
}

// Backwards-compatible alias for callers that still import the old
// name. New code should use ``fetchArtifact``.
export const fetchPdf = fetchArtifact;

// ---------------------------------------------------------------------
// Briefing-note dialog (ADR-0008)
// ---------------------------------------------------------------------

export interface JudgementAnswerPayload {
  answer: boolean | null;
  comment: string;
}

export interface BriefingNoteRenderRequest {
  principal_entity: string;
  contractor_entity: string;
  contract_title: string;
  contract_reference: string;
  variation_clause: string;
  variation_submission_ref: string;
  prepared_by: string;
  project_manager: string;
  super_proposed: JudgementAnswerPayload;
  for_convenience: JudgementAnswerPayload;
  mitigation_taken: JudgementAnswerPayload;
  scheduling_assessed: JudgementAnswerPayload;
  full_package_quoted: JudgementAnswerPayload;
}

export interface BriefingNoteContextResponse {
  principal_entity: string;
  contractor_entity: string;
  contract_title: string;
  contract_reference: string;
  variation_clause: string;
  variation_submission_ref: string;
  prepared_by: string;
  project_manager: string;
  variation_id: string;
}

export async function getBriefingNoteContext(
  cfg: ClientConfig,
  assessmentId: string,
): Promise<BriefingNoteContextResponse> {
  return request<BriefingNoteContextResponse>(
    cfg,
    `/v1/assessments/${encodeURIComponent(assessmentId)}/briefing-note/context`,
  );
}

export async function renderBriefingNote(
  cfg: ClientConfig,
  assessmentId: string,
  body: BriefingNoteRenderRequest,
): Promise<Blob> {
  // The render endpoint returns the .docx bytes directly so the
  // dialog can trigger the download without a follow-up GET.
  //
  // We can't use the shared ``request`` helper because it parses
  // the response as text/JSON; the docx body is binary. Mirror the
  // auth + CSRF + credentials behaviour by hand so the cookie
  // session is respected and the CSRF middleware on the backend
  // accepts the POST. Same-origin cookies attach via
  // ``credentials: "include"``; bearer-only callers still work
  // because the Authorization header is sent in parallel.
  const headers = new Headers({
    Authorization: `Bearer ${cfg.token}`,
    "Content-Type": "application/json",
  });
  const csrfToken = readCookie(COOKIE_NAME_CSRF);
  if (csrfToken !== null) {
    headers.set("X-CVP-CSRF", csrfToken);
  }
  const response = await fetch(
    `${cfg.baseUrl}/v1/assessments/${encodeURIComponent(assessmentId)}/briefing-note/render`,
    {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const errorBody = (await response.json()) as {
        code?: string;
        message?: string;
      };
      detail = errorBody.message ?? detail;
    } catch {
      // body not JSON; keep statusText
    }
    throw {
      code: "render_failed",
      message: detail,
      status: response.status,
    } satisfies ApiError;
  }
  return response.blob();
}
